import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from '../lib/i18n';
import styles from './LottieLoader.module.css';

// The branded loading animation: the designer's Lottie of the Symphony loop mark
// (the marks morphing square→circle around the ring, the lit point travelling
// clockwise). Served from `public/connecting-loader.json`; the player is the
// SVG-only lottie build.
//
// The animation art sits centered in a 1920×1080 canvas, so we crop the renderer
// to the mark's own bounding box — otherwise the ring renders tiny inside a mostly
// empty frame.
const VIEW_BOX = '801 386 300 300';

type Anim = { destroy(): void; goToAndPlay(value: number, isFrame?: boolean): void; currentFrame: number };
type Player = {
  loadAnimation(params: {
    container: Element;
    renderer?: 'svg';
    loop?: boolean;
    autoplay?: boolean;
    animationData?: unknown;
    rendererSettings?: { viewBoxOnly?: boolean; viewBoxSize?: string; preserveAspectRatio?: string };
  }): Anim;
};

// Player + parsed animation cached at module scope, loaded ONCE. The first loader
// pays the async import+fetch; every mount after that starts the animation
// synchronously (in a layout effect) so a remount — e.g. `DashboardView` swapping
// its single-loader return for the fragment that hosts the frame — shows the mark
// immediately instead of a blank beat (the "loading → empty → loading" flash).
let player: Player | null = null;
let animationData: unknown = null;
let preload: Promise<void> | null = null;

// The loop's current frame, shared across mounts, so a new loader resumes where the
// last left off instead of snapping to frame 0.
let sharedFrame = 0;

function preloadAssets(): Promise<void> {
  if (player && animationData != null) return Promise.resolve();
  if (!preload) {
    preload = Promise.all([
      import('lottie-web/build/player/lottie_light'),
      fetch('/connecting-loader.json').then((r) => r.json()),
    ]).then(([mod, data]) => {
      player = mod.default as unknown as Player;
      animationData = data;
    });
  }
  return preload;
}

// Kick the load off as soon as this module is imported on the client, so the assets
// are usually ready before the first loader even mounts.
if (typeof window !== 'undefined') void preloadAssets().catch(() => {});

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function LottieLoader({ size = 120 }: { size?: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLSpanElement>(null);

  useIsoLayoutEffect(() => {
    let cancelled = false;
    let anim: Anim | undefined;

    const start = () => {
      if (cancelled || !ref.current || !player || animationData == null) return;
      anim = player.loadAnimation({
        container: ref.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
        rendererSettings: { viewBoxOnly: true, viewBoxSize: VIEW_BOX, preserveAspectRatio: 'xMidYMid meet' },
      });
      anim.goToAndPlay(sharedFrame, true);
      // Fill the span so the mark scales with `size` (which flips on the narrow
      // breakpoint) rather than being pinned to its mount-time dimensions.
      const svg = ref.current.querySelector('svg');
      if (svg) {
        svg.style.width = '100%';
        svg.style.height = '100%';
      }
    };

    // Cached → start synchronously (no empty frame). First time → await the load.
    if (player && animationData != null) start();
    else void preloadAssets().then(start).catch(() => {});

    return () => {
      cancelled = true;
      if (anim) sharedFrame = anim.currentFrame || sharedFrame;
      anim?.destroy();
    };
  }, []);

  return (
    <span
      ref={ref}
      role="status"
      aria-label={t('symphony.loopLoader.label')}
      className={styles.mark}
      style={{ width: size, height: size, flexShrink: 0, pointerEvents: 'none' }}
    />
  );
}
